/**
 * FieldObservationCapture — the shared capture UI for the Field Observation tool
 * (Construction Gates Phase B/C — docs/FIELD_OBSERVATIONS.md).
 *
 * Extracted from FieldObservationModal.jsx so the SAME guide/notes/photos experience mounts in two
 * hosts with different data layers behind one `io` adapter:
 *   - the in-app full-screen modal (online adapter — buildOnlineIo below, straight service calls,
 *     Phase B behavior unchanged)
 *   - the standalone /field page (offline adapter — field/offline.js, local-first + sync queue)
 *
 * The io contract (every method may be async):
 *   load()                                  -> { observation, guide, drafts, freeNotes, offline? }
 *   saveItem(key, draft, { free })          -> persist an item snapshot (blur saves)
 *   addPhotos(key, draft, files, { free })  -> newDraft (adapter persists; photos gain path+url)
 *   removePhoto(key, draft, path, { free }) -> newDraft (adapter persists)
 *   toggleChecklist(itemId, complete)       -> tick the REAL checklist item (may throw → revert)
 *   createFreeNote()                        -> { id, note:'', locationTag:'', photos:[] }
 *   deleteItem(itemId)                      -> remove a free note (+ its photos)
 *   setStatus(observationId, status)        -> observation  (draft <-> issued)
 *   previewPdf()                            -> Blob
 *   caps()                                  -> { canIssue, canPdf, note? } — host capabilities
 */
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { getMilestoneChecklistItems, completeChecklistItem } from '../services/checklist.js'
import {
  getOrCreateFieldObservation, uploadFieldObservationPhoto, upsertFieldObservationItem,
  deleteFieldObservationItem, setFieldObservationStatus, previewFieldObservationPdf,
} from '../services/fieldObservations.js'
import { confirmDialog } from './Dialog.jsx'
import { resizedImageUrl } from '../lib/imageUrl.js'
import { CAT_ORDER, catLabel } from '../outline/constants.js'

const getFirst = v => (Array.isArray(v) ? v[0] : v)
// The capture guide only shows Execution-category items — Full-Kit (scheduling, plan review),
// QA/QC (internal report review before issue), and Wrap-Up (issuing the report, logging
// corrections) are all office/coordination tasks that happen before or after the site walk, not
// things to note-and-photograph while standing on site. Those items are still real Checklist
// Items and remain fully completable from the milestone's own checklist in the app — nothing is
// removed, just not shown in this on-site capture surface. Falls back to showing everything if a
// milestone type's checklist happens to have no Execution items, so the tool is never blank.
const CAPTURE_CATEGORY = 'Execution'
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
  const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
}))

/* ── The ONLINE adapter — Phase B's exact behavior, packaged ─────────────────── */
export function buildOnlineIo({ msId, projectId, memberId, byName }) {
  return {
    async load() {
      const [obsRes, clItems] = await Promise.all([
        getOrCreateFieldObservation(msId, projectId),
        getMilestoneChecklistItems(msId),
      ])
      const guide = (clItems || []).map(r => {
        const f = r.fields
        return {
          id: r.id,
          name: getFirst(f['Name for List (from Deliverable Checklist Item Template)']) || 'Item',
          summary: getFirst(f['Summary (from Deliverable Checklist Item Template)']) || '',
          category: getFirst(f['Category (from Deliverable Checklist Item Template)']) || 'Other',
          complete: f['Complete'] === true,
          notRequired: f['Milestone Not Required'] === true,
        }
      })
      const drafts = {}
      const freeNotes = []
      for (const it of (obsRes.items || [])) {
        const d = {
          note: it.note || '', locationTag: it.location_tag || '',
          photos: (it.photo_paths || []).map((p, i) => ({ path: p, url: (it.photoUrls || [])[i] || p, caption: (it.photo_captions || [])[i] || '' })),
        }
        if (it.checklist_item_airtable_id) drafts[it.checklist_item_airtable_id] = d
        else freeNotes.push({ id: it.id, ...d })
      }
      guide.forEach(g => { if (!drafts[g.id]) drafts[g.id] = { note: '', locationTag: '', photos: [] } })
      this._obsId = obsRes.observation.id
      this._guide = guide
      return { observation: obsRes.observation, guide, drafts, freeNotes, offline: false }
    },
    saveItem(key, draft, { free = false } = {}) {
      const g = free ? null : this._guide.find(x => x.id === key)
      return upsertFieldObservationItem(this._obsId, {
        id: key, checklistItemId: free ? null : key, category: free ? null : (g ? g.category : null),
        note: draft.note, locationTag: draft.locationTag,
        photoPaths: (draft.photos || []).map(p => p.path),
        photoCaptions: (draft.photos || []).map(p => p.caption || ''),
      }, byName)
    },
    async addPhotos(key, draft, files, opts = {}) {
      const added = []
      for (const file of files) {
        const path = await uploadFieldObservationPhoto(this._obsId, file)
        added.push({ path, url: URL.createObjectURL(file), caption: '' })
      }
      const next = { ...draft, photos: [...(draft.photos || []), ...added] }
      await this.saveItem(key, next, opts)
      return next
    },
    async removePhoto(key, draft, path, opts = {}) {
      const next = { ...draft, photos: (draft.photos || []).filter(p => p.path !== path) }
      await this.saveItem(key, next, opts)
      return next
    },
    async toggleChecklist(itemId, complete) {
      await completeChecklistItem(itemId, complete, memberId)
      // Best-effort: keep the outline's own checklist cache in sync if this milestone happens to
      // also be open in the background (same pattern as other cross-tool invalidations in the app).
      if (ctx.clCache && Array.isArray(ctx.clCache[msId])) {
        const rec = ctx.clCache[msId].find(r => r.id === itemId)
        if (rec) {
          rec.fields['Complete'] = complete
          rec.fields['Completed by'] = (complete && memberId) ? [memberId] : []
        }
        window.dispatchEvent(new Event('outline-render'))
      }
    },
    createFreeNote() { return { id: uuid(), note: '', locationTag: '', photos: [] } },
    deleteItem(itemId) { return deleteFieldObservationItem(itemId) },
    setStatus(observationId, status) { return setFieldObservationStatus(observationId, status) },
    previewPdf() { return previewFieldObservationPdf(msId) },
    caps() { return { canIssue: true, canPdf: true } },
  }
}

/* ── Presentational pieces (moved verbatim from the Phase B modal) ───────────── */

const THUMB_W_PX = 200   // the strip's 84×76 box, ~2.4x for retina — far lighter than the original
const VIEW_W_PX = 1800   // lightbox — crisp enough to read what's in frame, still transformed

// A photo's <img src> is served through Supabase's on-the-fly resize; if the transform 403s (or
// the plan doesn't have it) this falls back to the untransformed public url, then to p.path (the
// pre-existing local-blob fallback) — same escalation RenderingBookViewer.jsx already uses.
function onPhotoImgError(e, fallbackUrl, path) {
  if (e.currentTarget.dataset.fb === '2') return
  if (e.currentTarget.dataset.fb === '1') { e.currentTarget.dataset.fb = '2'; if (fallbackUrl !== path && !String(path).startsWith('local:')) e.currentTarget.src = path; return }
  e.currentTarget.dataset.fb = '1'
  e.currentTarget.src = fallbackUrl
}

// Full-res enlarge for a photo strip thumbnail. Portaled to <body>; Esc or a click on the scrim
// closes. Deliberately plain/inline-styled (no dependency on present/Lightbox.jsx's scoped CSS,
// which only loads on the /present surface) so it works identically in the in-app modal AND the
// standalone /field page.
function PhotoLightbox({ url, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])
  if (!url) return null
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.82)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <img src={resizedImageUrl(url, { width: VIEW_W_PX, quality: 85 })} onError={e => onPhotoImgError(e, url, url)}
        alt="" onClick={e => e.stopPropagation()}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4, boxShadow: '0 8px 30px rgba(0,0,0,0.5)' }} />
      <button onClick={onClose} title="Close" aria-label="Close" style={{
        position: 'fixed', top: 16, right: 20, width: 34, height: 34, borderRadius: '50%', border: 'none',
        background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 16, cursor: 'pointer',
      }}>&#10005;</button>
    </div>,
    document.body
  )
}

// onCaptionChange is optional — when a caller passes it (Field Observation only, so far), each
// photo gets its own small location input beneath the thumbnail. Without it (punch capture),
// captions simply don't render — a photo strip with multiple shots and no per-photo captioning is
// the pre-existing, still-valid behavior there.
function PhotoStrip({ photos, onRemove, onCaptionChange, locked }) {
  const [preview, setPreview] = useState(null)
  if (!photos.length) return null
  async function handleRemove(path) {
    if (!(await confirmDialog({ title: 'Remove photo?', message: 'This photo will be permanently deleted — this cannot be undone.', confirmLabel: 'Remove', danger: true }))) return
    onRemove(path)
  }
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {photos.map((p, i) => (
          <div key={p.path + i} style={{ width: 84, flexShrink: 0 }}>
            <div style={{ position: 'relative', width: 84, height: 76, borderRadius: 6, overflow: 'hidden', border: '0.5px solid var(--aj-border)', cursor: 'zoom-in' }}
              onClick={() => setPreview(p.url)} title="View photo">
              <img src={resizedImageUrl(p.url, { width: THUMB_W_PX, quality: 60 })} alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={e => onPhotoImgError(e, p.url, p.path)} />
              {!locked && (
                <button onClick={e => { e.stopPropagation(); handleRemove(p.path) }} title="Remove photo" style={{
                  position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none',
                  background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, lineHeight: '18px', textAlign: 'center', cursor: 'pointer', padding: 0,
                }}>&#10005;</button>
              )}
            </div>
            {onCaptionChange && (
              <input
                value={p.caption || ''}
                disabled={locked}
                placeholder="Photo location…"
                onChange={e => onCaptionChange(i, e.target.value, false)}
                onBlur={e => onCaptionChange(i, e.target.value, true)}
                style={{
                  width: '100%', marginTop: 4, boxSizing: 'border-box', fontFamily: '"DM Mono", monospace', fontSize: 10,
                  padding: '3px 5px', borderRadius: 4, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)',
                }}
              />
            )}
          </div>
        ))}
      </div>
      {preview && <PhotoLightbox url={preview} onClose={() => setPreview(null)} />}
    </>
  )
}

// A plain <textarea> that grows to fit its content instead of a manual drag-resize handle (same
// scrollHeight technique as GuideItemCard's own note field / ScheduleEditor.jsx's AutoCell /
// Dialog.jsx's prompt textarea). Needed as its own component — not inlined — because freeNotes
// renders inline inside a .map(), where a hook can't be called directly.
function AutoGrowTextarea({ value, style, ...props }) {
  const ref = useRef(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }, [value])
  return <textarea ref={ref} value={value} rows={1} style={{ minHeight: 46, resize: 'none', overflow: 'hidden', ...style }} {...props} />
}

function GuideItemCard({ g, draft, onChange, onToggleComplete, onUploadPhotos, onRemovePhoto, locked, busy }) {
  const fileRef = useRef(null)
  return (
    <div style={{ padding: '12px 14px', borderRadius: 8, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-surface)', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', marginTop: 2, cursor: locked ? 'default' : 'pointer' }}>
          <input type="checkbox" checked={g.complete} disabled={locked || busy} onChange={onToggleComplete}
            style={{ width: 18, height: 18, cursor: locked ? 'default' : 'pointer' }} />
        </label>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 500, color: 'var(--aj-text)', textDecoration: g.complete ? 'line-through' : 'none', opacity: g.complete ? 0.7 : 1 }}>
            {g.name}
          </div>
          {g.summary && <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 2 }}>{g.summary}</div>}
          <AutoGrowTextarea
            value={draft.note}
            disabled={locked}
            placeholder="Observation note…"
            onChange={e => onChange({ note: e.target.value })}
            onBlur={() => onChange({ note: draft.note }, true)}
            style={{
              width: '100%', marginTop: 8, fontFamily: '"DM Sans", sans-serif', fontSize: 13,
              padding: '7px 9px', borderRadius: 6, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)',
            }}
          />
          <input
            value={draft.locationTag}
            disabled={locked}
            placeholder="General location (optional)"
            onChange={e => onChange({ locationTag: e.target.value })}
            onBlur={() => onChange({ locationTag: draft.locationTag }, true)}
            style={{
              width: '100%', marginTop: 6, fontFamily: '"DM Mono", monospace', fontSize: 12,
              padding: '6px 9px', borderRadius: 6, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)',
            }}
          />
          <PhotoStrip photos={draft.photos} onRemove={onRemovePhoto} locked={locked}
            onCaptionChange={(idx, val, commit) => {
              const nextPhotos = draft.photos.map((p, i) => i === idx ? { ...p, caption: val } : p)
              onChange({ photos: nextPhotos }, commit)
            }}
          />
          {!locked && (
            <>
              <button onClick={() => fileRef.current && fileRef.current.click()} disabled={busy} style={{
                marginTop: 8, fontSize: 11.5, fontFamily: '"Montserrat", sans-serif', textTransform: 'uppercase', letterSpacing: '0.06em',
                padding: '6px 12px', borderRadius: 20, border: '0.5px solid var(--aj-accent)', background: 'transparent', color: 'var(--aj-accent)', cursor: 'pointer',
              }}>{busy ? 'Adding…' : '+ Photo'}</button>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple hidden
                onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) onUploadPhotos(files) }} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── The capture body + footer (state machine shared by both hosts) ──────────── */

export default function FieldObservationCapture({ io, onObservation, onOffline }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [observation, setObservation] = useState(null)
  const [guide, setGuide] = useState([])
  const [drafts, setDrafts] = useState({})
  const [freeNotes, setFreeNotes] = useState([])
  const [busyKey, setBusyKey] = useState(null)
  const [savingBanner, setSavingBanner] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    io.load().then(res => {
      if (cancelled) return
      setObservation(res.observation)
      setGuide(res.guide)
      setDrafts(res.drafts)
      setFreeNotes(res.freeNotes)
      setLoading(false)
      if (onObservation) onObservation(res.observation)
      if (onOffline) onOffline(!!res.offline)
    }).catch(err => { if (!cancelled) { setError(err.message || String(err)); setLoading(false) } })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [io])

  const locked = observation && observation.status === 'issued'
  const caps = io.caps()

  function flashSaved() { setSavingBanner(true); setTimeout(() => setSavingBanner(false), 900) }
  const fail = err => setError(err && err.message ? err.message : String(err))

  function updateDraft(g, patch, save) {
    // The side effect (io.saveItem) must NOT live inside the setState updater callback — React
    // (StrictMode, dev only) double-invokes updater functions to catch impure ones, which would
    // silently double-fire the save/queue op. Compute `next` from the current closure state and
    // save it explicitly, outside the updater.
    const next = { ...drafts[g.id], ...patch }
    setDrafts(d => ({ ...d, [g.id]: next }))
    if (save) io.saveItem(g.id, next).then(flashSaved).catch(fail)
  }

  async function toggleComplete(g) {
    const next = !g.complete
    setGuide(list => list.map(gi => gi.id === g.id ? { ...gi, complete: next } : gi))
    try { await io.toggleChecklist(g.id, next) } catch (err) {
      setGuide(list => list.map(gi => gi.id === g.id ? { ...gi, complete: !next } : gi))
      fail(err)
    }
  }

  async function uploadPhotos(g, files) {
    setBusyKey(g.id)
    try {
      const next = await io.addPhotos(g.id, drafts[g.id] || { note: '', locationTag: '', photos: [] }, files)
      setDrafts(d => ({ ...d, [g.id]: next }))
      flashSaved()
    } catch (err) { fail(err) }
    setBusyKey(null)
  }

  async function removePhoto(g, path) {
    try {
      const next = await io.removePhoto(g.id, drafts[g.id], path)
      setDrafts(d => ({ ...d, [g.id]: next }))
    } catch (err) { fail(err) }
  }

  async function addFreeNote() {
    const n = await io.createFreeNote()
    setFreeNotes(list => [...list, n])
  }
  function updateFreeNote(id, patch, save) {
    // Same StrictMode-safety fix as updateDraft above — save is a side effect, kept outside the
    // setState updater so it can never be invoked twice.
    const existing = freeNotes.find(n => n.id === id)
    if (!existing) return
    const next = { ...existing, ...patch }
    setFreeNotes(list => list.map(n => n.id === id ? next : n))
    if (save) io.saveItem(id, next, { free: true }).then(flashSaved).catch(fail)
  }
  async function uploadFreePhotos(id, files) {
    setBusyKey(id)
    try {
      const n = freeNotes.find(x => x.id === id)
      const next = await io.addPhotos(id, n, files, { free: true })
      setFreeNotes(list => list.map(x => x.id === id ? { id, ...next } : x))
      flashSaved()
    } catch (err) { fail(err) }
    setBusyKey(null)
  }
  async function removeFreePhoto(id, path) {
    try {
      const n = freeNotes.find(x => x.id === id)
      const next = await io.removePhoto(id, n, path, { free: true })
      setFreeNotes(list => list.map(x => x.id === id ? { id, ...next } : x))
    } catch (err) { fail(err) }
  }
  async function deleteFreeNote(id) {
    if (!(await confirmDialog({ title: 'Remove note?', message: 'This note and its photos will be removed.', confirmLabel: 'Remove', danger: true }))) return
    setFreeNotes(list => list.filter(n => n.id !== id))
    try { await io.deleteItem(id) } catch (err) { fail(err) }
  }

  async function handlePreviewPdf() {
    try {
      const blob = await io.previewPdf()
      window.open(URL.createObjectURL(blob), '_blank', 'noopener')
    } catch (err) { fail(err) }
  }
  async function handleIssue() {
    if (!observation) return
    if (!(await confirmDialog({ title: 'Mark reviewed & issue?', message: 'This locks the observation for editing. It becomes the copy that publishes to Documents and the Client Hub when the milestone posts.', confirmLabel: 'Issue' }))) return
    try {
      const obs = await io.setStatus(observation.id, 'issued')
      setObservation(obs)
      if (onObservation) onObservation(obs)
    } catch (err) { fail(err) }
  }
  async function handleReopen() {
    if (!observation) return
    if (!(await confirmDialog({ title: 'Reopen for editing?', message: 'The observation returns to draft and can be edited again.', confirmLabel: 'Reopen' }))) return
    try {
      const obs = await io.setStatus(observation.id, 'draft')
      setObservation(obs)
      if (onObservation) onObservation(obs)
    } catch (err) { fail(err) }
  }

  const requiredGuide = guide.filter(g => !g.notRequired)
  const executionGuide = requiredGuide.filter(g => g.category === CAPTURE_CATEGORY)
  const usingExecutionOnly = executionGuide.length > 0
  const displayGuide = usingExecutionOnly ? executionGuide : requiredGuide
  const hiddenCount = usingExecutionOnly ? requiredGuide.length - executionGuide.length : 0

  const byCat = {}
  displayGuide.forEach(g => { (byCat[g.category] = byCat[g.category] || []).push(g) })
  const catOrder = Object.keys(byCat).sort((a, b) => {
    const ai = CAT_ORDER.indexOf(a), bi = CAT_ORDER.indexOf(b)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  return (
    <>
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px 120px', maxWidth: 720, margin: '0 auto', width: '100%' }}>
        {loading && <div style={{ color: 'var(--aj-text-muted)', padding: 20 }}>Loading…</div>}
        {error && <div style={{ color: '#b3402f', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {!loading && locked && (
          <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic', marginBottom: 14 }}>
            Issued — reopen to make changes.
          </div>
        )}
        {!loading && !error && hiddenCount > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
            Showing on-site inspection items only — scheduling, review, and sign-off steps for this
            gate are tracked in the milestone's checklist in the app.
          </div>
        )}
        {!loading && !error && catOrder.map(cat => (
          <div key={cat} style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 }}>{catLabel(cat)}</div>
            {byCat[cat].map(g => (
              <GuideItemCard key={g.id} g={g} draft={drafts[g.id] || { note: '', locationTag: '', photos: [] }}
                locked={locked} busy={busyKey === g.id}
                onChange={(patch, save) => updateDraft(g, patch, save)}
                onToggleComplete={() => toggleComplete(g)}
                onUploadPhotos={files => uploadPhotos(g, files)}
                onRemovePhoto={path => removePhoto(g, path)}
              />
            ))}
          </div>
        ))}
        {!loading && !error && (
          <div>
            <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 }}>Additional Site Notes</div>
            {freeNotes.map(n => (
              <div key={n.id} style={{ padding: '12px 14px', borderRadius: 8, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-surface)', marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <AutoGrowTextarea value={n.note} disabled={locked} placeholder="Note…"
                      onChange={e => updateFreeNote(n.id, { note: e.target.value })}
                      onBlur={() => updateFreeNote(n.id, { note: n.note }, true)}
                      style={{ width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 13, padding: '7px 9px', borderRadius: 6, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)' }} />
                    <input value={n.locationTag} disabled={locked} placeholder="Location tag (optional)"
                      onChange={e => updateFreeNote(n.id, { locationTag: e.target.value })}
                      onBlur={() => updateFreeNote(n.id, { locationTag: n.locationTag }, true)}
                      style={{ width: '100%', marginTop: 6, fontFamily: '"DM Mono", monospace', fontSize: 12, padding: '6px 9px', borderRadius: 6, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)' }} />
                    <PhotoStrip photos={n.photos} onRemove={path => removeFreePhoto(n.id, path)} locked={locked}
                      onCaptionChange={(idx, val, commit) => {
                        const nextPhotos = n.photos.map((p, i) => i === idx ? { ...p, caption: val } : p)
                        updateFreeNote(n.id, { photos: nextPhotos }, commit)
                      }}
                    />
                    {!locked && (
                      <label style={{
                        display: 'inline-block', marginTop: 8, fontSize: 11.5, fontFamily: '"Montserrat", sans-serif', textTransform: 'uppercase', letterSpacing: '0.06em',
                        padding: '6px 12px', borderRadius: 20, border: '0.5px solid var(--aj-accent)', color: 'var(--aj-accent)', cursor: 'pointer',
                      }}>
                        {busyKey === n.id ? 'Adding…' : '+ Photo'}
                        <input type="file" accept="image/*" capture="environment" multiple hidden
                          onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) uploadFreePhotos(n.id, files) }} />
                      </label>
                    )}
                  </div>
                  {!locked && <button onClick={() => deleteFreeNote(n.id)} title="Remove note" style={{ background: 'none', border: 'none', color: 'var(--aj-text-muted)', cursor: 'pointer', fontSize: 15 }}>&#10005;</button>}
                </div>
              </div>
            ))}
            {!locked && (
              <button onClick={addFreeNote} style={{
                fontSize: 12, fontFamily: '"DM Sans", sans-serif', padding: '8px 14px', borderRadius: 6,
                border: '0.5px dashed var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer',
              }}>+ Add general note</button>
            )}
          </div>
        )}
      </div>

      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, background: 'var(--aj-bg-surface)', borderTop: '0.5px solid var(--aj-border)',
        padding: '12px 20px', display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap',
      }}>
        {savingBanner && <span style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginRight: 4 }}>Saved</span>}
        {caps.note && <span style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginRight: 4 }}>{caps.note}</span>}
        {caps.canPdf && <button onClick={handlePreviewPdf} style={btnStyle(false)}>Preview PDF</button>}
        {caps.canIssue && (!locked
          ? <button onClick={handleIssue} disabled={!observation} style={btnStyle(true)}>Mark reviewed &amp; issue</button>
          : <button onClick={handleReopen} style={btnStyle(false)}>Reopen (mark draft)</button>)}
      </div>
    </>
  )
}

function btnStyle(primary) {
  return {
    fontFamily: '"Montserrat", sans-serif', fontSize: 11.5, letterSpacing: '0.06em', textTransform: 'uppercase',
    padding: '10px 18px', borderRadius: 20, cursor: 'pointer',
    border: primary ? 'none' : '0.5px solid var(--aj-border-mid)',
    background: primary ? '#07272D' : 'transparent',
    color: primary ? '#fff' : 'var(--aj-text)',
  }
}

export { PhotoStrip, AutoGrowTextarea, GuideItemCard, CAT_ORDER, uuid }
